import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import { DatePipe } from "@angular/common";
import { Subscription } from "rxjs";
import { ButtonDirective } from "primeng/button";
import { InputText } from "primeng/inputtext";

import { UserRepository } from "../user.repository";
import { NotifyService } from "../../notify/notify.service";
import { TOAST } from "../../notify/toast/toast-type.enum";
import { BackendErrorPipe } from "../../shared/backend/backend-error.pipe";
import { ClipboardService } from "../../shared/clipboard/clipboard.service";

/**
 * The signed-in user's connection for an AI assistant (Claude) over MCP. The connector URL
 * carries the credential and is shown once, right after it is issued; the profile otherwise
 * only knows whether a connection exists.
 */
@Component({
  selector: "app-user-form-ai-assistant",
  templateUrl: "./user-form-ai-assistant.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonDirective, InputText, DatePipe],
})
export class UserFormAiAssistantComponent implements OnInit, OnDestroy {
  private userRepository = inject(UserRepository);
  private notifyService = inject(NotifyService);
  private clipboardService = inject(ClipboardService);

  public readonly connected = signal(false);
  public readonly createdAt = signal<string | null>(null);
  public readonly url = signal<string | null>(null);
  public readonly loading = signal(false);
  private subscription = new Subscription();

  ngOnInit() {
    this.subscription.add(
      this.userRepository.getMcpToken().subscribe({
        next: ({ connected, createdAt }) => {
          this.connected.set(connected);
          this.createdAt.set(createdAt);
        },
        error: () => this.connected.set(false),
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public connect(): void {
    this.loading.set(true);
    this.subscription.add(
      this.userRepository.createMcpToken().subscribe({
        next: ({ url, createdAt }) => {
          this.loading.set(false);
          this.connected.set(true);
          this.createdAt.set(createdAt);
          this.url.set(url);
        },
        error: (err) => {
          this.loading.set(false);
          this.notifyService.toast(
            TOAST.ERROR,
            "Error",
            new BackendErrorPipe().transform(err),
          );
        },
      }),
    );
  }

  public disconnect(): void {
    this.loading.set(true);
    this.subscription.add(
      this.userRepository.deleteMcpToken().subscribe({
        next: () => {
          this.loading.set(false);
          this.connected.set(false);
          this.createdAt.set(null);
          this.url.set(null);
        },
        error: (err) => {
          this.loading.set(false);
          this.notifyService.toast(
            TOAST.ERROR,
            "Error",
            new BackendErrorPipe().transform(err),
          );
        },
      }),
    );
  }

  public copyUrl(): void {
    const url = this.url();
    if (url) {
      this.clipboardService.copyToClipboard(url);
    }
  }
}
